import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Box,
    IconButton,
    Paper,
    Slider,
    Typography,
    Collapse,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

const MUSIC_SRC = '/music/The%20Next%20Level%20Mabar.mp3';
const AUTO_COLLAPSE_MS = 3500;

interface BackgroundMusicPlayerProps {
    mode: 'light' | 'dark';
}

const BackgroundMusicPlayer: React.FC<BackgroundMusicPlayerProps> = ({ mode }) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [needsUserInteraction, setNeedsUserInteraction] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const userGestureHandled = useRef(false);
    const autoCollapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const play = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.play().then(() => {
            setIsPlaying(true);
            setHasInteracted(true);
            setNeedsUserInteraction(false);
            setExpanded(true);
            if (autoCollapseTimer.current) clearTimeout(autoCollapseTimer.current);
            autoCollapseTimer.current = setTimeout(() => {
                setExpanded(false);
                autoCollapseTimer.current = null;
            }, AUTO_COLLAPSE_MS);
        }).catch((err) => {
            if (err.name === 'NotAllowedError') {
                setNeedsUserInteraction(true);
            }
        });
    }, []);

    const pause = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.pause();
        setIsPlaying(false);
    }, []);

    const togglePlay = useCallback(() => {
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    }, [isPlaying, play, pause]);

    useEffect(() => () => {
        if (autoCollapseTimer.current) clearTimeout(autoCollapseTimer.current);
    }, []);

    // Coba autoplay saat mount
    useEffect(() => {
        const audio = new Audio(MUSIC_SRC);
        audio.loop = true;
        audio.volume = 1;
        audioRef.current = audio;

        audio.play().then(() => {
            setIsPlaying(true);
            setHasInteracted(true);
            setExpanded(true);
            if (autoCollapseTimer.current) clearTimeout(autoCollapseTimer.current);
            autoCollapseTimer.current = setTimeout(() => {
                setExpanded(false);
                autoCollapseTimer.current = null;
            }, AUTO_COLLAPSE_MS);
        }).catch((err) => {
            if (err.name === 'NotAllowedError') {
                setNeedsUserInteraction(true);
            }
        });

        const handleTimeUpdate = () => setProgress(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
            audio.pause();
            audioRef.current = null;
        };
    }, []);

    // Fallback: klik/sentuh pertama di mana saja untuk memulai jika autoplay diblokir
    useEffect(() => {
        if (!needsUserInteraction || userGestureHandled.current) return;

        const handleUserGesture = () => {
            if (userGestureHandled.current) return;
            userGestureHandled.current = true;
            play();
        };

        const events = ['click', 'touchstart', 'keydown'];
        const opts = { passive: true, once: true } as AddEventListenerOptions;

        events.forEach((ev) => document.addEventListener(ev, handleUserGesture, opts));
        return () => {
            events.forEach((ev) => document.removeEventListener(ev, handleUserGesture));
        };
    }, [needsUserInteraction, play]);

    // Sync volume dengan state
    useEffect(() => {
        const audio = audioRef.current;
        if (audio) audio.volume = isMuted ? 0 : volume;
    }, [volume, isMuted]);

    const handleSeek = (_: Event, value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        const audio = audioRef.current;
        if (audio && duration > 0) {
            audio.currentTime = (v / 100) * duration;
            setProgress(audio.currentTime);
        }
    };

    const formatTime = (sec: number) => {
        if (!sec || !isFinite(sec)) return '0:00';
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const isDark = mode === 'dark';

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 16,
                left: 16,
                zIndex: 1300,
                display: 'flex',
                alignItems: 'flex-end',
                transition: 'all 0.3s ease',
            }}
        >
            <Paper
                elevation={4}
                sx={{
                    overflow: 'hidden',
                    background: isDark
                        ? 'linear-gradient(135deg, rgba(18, 18, 18, 0.95) 0%, rgba(10, 22, 38, 0.95) 100%)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(227,242,253,0.95) 100%)',
                    backdropFilter: 'blur(12px)',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.3)',
                    borderRadius: expanded ? 3 : '50%',
                    boxShadow: isDark
                        ? '0 8px 32px rgba(0,0,0,0.4)'
                        : '0 8px 32px rgba(25,118,210,0.2)',
                    transition: 'all 0.3s ease',
                    minWidth: expanded ? 280 : 56,
                    width: expanded ? 'auto' : 56,
                    height: expanded ? 'auto' : 56,
                    minHeight: 56,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        height: 56,
                        cursor: 'pointer',
                        minWidth: expanded ? 268 : 56,
                    }}
                    onClick={() => {
                        if (!expanded) setExpanded(true);
                        else setExpanded(false);
                    }}
                >
                    <IconButton
                        size="medium"
                        onClick={(e) => {
                            e.stopPropagation();
                            togglePlay();
                        }}
                        sx={{
                            background: isDark
                                ? 'rgba(100,181,246,0.2)'
                                : 'rgba(25,118,210,0.15)',
                            color: isDark ? '#90caf9' : '#1976d2',
                            flexShrink: 0,
                            '&:hover': {
                                background: isDark
                                    ? 'rgba(100,181,246,0.35)'
                                    : 'rgba(25,118,210,0.25)',
                            },
                        }}
                    >
                        {isPlaying ? (
                            <PauseIcon fontSize="medium" />
                        ) : (
                            <PlayArrowIcon fontSize="medium" />
                        )}
                    </IconButton>
                    {expanded && (
                        <>
                            <MusicNoteIcon
                                sx={{
                                    color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                    fontSize: 20,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                                    fontWeight: 500,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                The Next Level Mabar
                            </Typography>
                            {needsUserInteraction && !hasInteracted && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: isDark ? '#90caf9' : '#1976d2',
                                        fontSize: '0.7rem',
                                        fontStyle: 'italic',
                                        flexShrink: 0,
                                    }}
                                >
                                    Klik untuk mulai
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                <Collapse in={expanded}>
                <Box sx={{ px: 2, pb: 1.5, pt: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', minWidth: 36 }}>
                            {formatTime(progress)}
                        </Typography>
                        <Slider
                            size="small"
                            value={duration > 0 ? (progress / duration) * 100 : 0}
                            onChange={handleSeek}
                            sx={{
                                color: isDark ? '#90caf9' : '#1976d2',
                                '& .MuiSlider-thumb': { width: 12, height: 12 },
                            }}
                        />
                        <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', minWidth: 36 }}>
                            {formatTime(duration)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <IconButton
                            size="small"
                            onClick={() => setIsMuted((m) => !m)}
                            sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}
                        >
                            {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                        </IconButton>
                        <Slider
                            size="small"
                            value={isMuted ? 0 : volume * 100}
                            onChange={(_, v) => {
                                const val = Array.isArray(v) ? v[0] : v;
                                setVolume(val / 100);
                                if (val > 0) setIsMuted(false);
                            }}
                            sx={{
                                maxWidth: 80,
                                color: isDark ? '#90caf9' : '#1976d2',
                                '& .MuiSlider-thumb': { width: 10, height: 10 },
                            }}
                        />
                    </Box>
                </Box>
            </Collapse>
        </Paper>
        </Box>
    );
};

export default BackgroundMusicPlayer;
